/* eslint-env browser */
import {
  apply, put, cancelled, delay, take, select,
} from 'redux-saga/effects';
import moment from 'moment';
import durationToString from '../util/duration-to-string';

import config from '../../run-config';


import {
  selectLocation,
  selectReport,
  loadLocationsAction,
  loadReportData as loadReportDataAction,
  loadReportDataSucceeded,
  loadReportDataFailed,
  selectLocationOnLoad as selectLocationOnLoadAction,
  selectReportOnLoad as selectReportOnLoadAction,
  loadAllReportData as loadAllReportDataAction,
} from '../reducers/reports.reducers';
import {
  setDateRange,
  setUseDefaultDateRange,
} from '../reducers/filter.reducers';
import {
  addMessage,
  removeMessagesByType,
} from '../reducers/message.reducers';
import { comparisonOptionNames } from '../models';

const resolveReportAssignments = (reportAssignments, reports) => reportAssignments.map((ra) => {
  const report = reports.find(r => r.reportID === ra.reportId);
  return {
    ...report,
    startOffset: ra.startOffset || report.startOffset,
    endOffset: ra.endOffset || report.endOffset,
  };
});

export function* loadLocations(reportsApi) {
  const debug = yield select(state => state.app.debug);

  try {
    const response = yield apply(reportsApi, reportsApi.loadLocations);

    if (response.errorCode || response.length === 0) {
      // yield put(addMessage({
      //   message: 'Es konnten keine zugeordneten Berichte gefunden werden', type: 'noReports', level: 'error',
      // }));

      yield put(loadLocationsAction.FAILED());
      return;
    }

    const { locations, reports } = response;

    const locationsWithReports = locations.map(l => ({
      ...l,
      reports: resolveReportAssignments(l.reportAssignments, reports),
    }));

    const [defaultLocation] = locationsWithReports;
    const defaultReport = defaultLocation.reportAssignments[0].reportId;

    yield put(selectLocationOnLoadAction(defaultLocation.id));
    yield put(selectReportOnLoadAction(defaultReport.reportID));

    yield put(loadLocationsAction.DONE({
      locations: locationsWithReports,
      reports,
    }));
  } catch (error) {
    //
  }
}

export function* loadReportData(reportsApi, { payload }) {
  yield delay(1000);
  const abortController = new AbortController();
  try {
    const filterOptions = yield select(state => state.filter.filterOptions);

    const payloadWithFilterOptions = {
      ...payload,
      options: {
        ...payload.options,
        ...filterOptions,
      },
    };

    const response = yield apply(reportsApi, reportsApi.loadReportData, [payloadWithFilterOptions, abortController.signal]);

    if (response.errorCode || response.errorMessage) {
      yield put(loadReportDataFailed(payload.widgetId));
    } else {
      yield put(loadReportDataSucceeded({
        reportDataId: payload.reportDataId,
        reportData: response,
        locationId: payload.options.locationId,
        widgetId: payload.widgetId,
      }));
    }
  } catch {
    yield put(loadReportDataFailed(payload.widgetId));
  } finally {
    if (yield cancelled()) {
      abortController.abort();
    }
  }
}

export function* selectLocationOnLoad({ payload }) {
  yield take(loadLocationsAction.DONE);
  yield put(selectLocation(Number.parseInt(payload, 10)));
}

export function* selectReportOnLoad({ payload }) {
  yield take(loadLocationsAction.DONE);
  yield put(selectReport(Number.parseInt(payload, 10)));
  yield put(setUseDefaultDateRange(true));
}

export function* setDateRangeOnSelectReport() {
  const useDefaultDateRange = yield select(state => state.filter.useDefaultDateRange);

  if (useDefaultDateRange) {
    const selectedLocation = yield select(state => state.reports.selectedLocation);
    const selectedReport = yield select(state => state.reports.selectedReport);

    if (!selectedReport) {
      return;
    }

    const dayStart = (selectedLocation && selectedLocation.virtualDayStart != null)
      ? moment.duration(selectedLocation.virtualDayStart) : moment.duration(5, 'hours');

    const currentDay = moment().hour(dayStart.hours()).minutes(dayStart.minutes()).startOf('minute')
      .unix();

    const startOffset = Number.isInteger(selectedReport.startOffset)
      ? selectedReport.startOffset : moment.duration(selectedReport.startOffset).asSeconds();
    const endOffset = Number.isInteger(selectedReport.endOffset)
      ? selectedReport.endOffset : moment.duration(selectedReport.endOffset).asSeconds();

    yield put(setDateRange({
      startDate: currentDay + startOffset,
      endDate: currentDay + endOffset,
    }));
    yield put(setUseDefaultDateRange(true));
  }
}

export function* loadTpsStatus(reportsApi, { payload }) {
  if (config.tpsStatusFeature === 'true') {
    yield put(removeMessagesByType('tpsLastSeen'));
    const response = yield apply(reportsApi, reportsApi.loadTpsStatus, [payload]);
    if (response.errorCode) {
      yield put(addMessage({ message: 'Kassa konnte nicht erreicht werden', type: 'tpsLastSeen', level: 'error' }));
      return;
    }

    const lastSeen = moment.utc(response.aliveTime);
    const timeSinceLastSeen = moment.duration(moment().diff(lastSeen));

    if (timeSinceLastSeen > moment.duration(30, 'minutes')) {
      const message = `Letzte Meldung der Kassa vor ${durationToString(timeSinceLastSeen)},
        bitte vergewissern Sie sich, dass Ihre Kassa mit dem Internet verbunden ist.`;
      yield put(addMessage({ message, type: 'tpsLastSeen', level: 'warning' }));
    }
  }
}

export function* selectReportByWidgetId({ payload }) {
  const reports = yield select(state => state.reports.selectedLocation.reports);

  const report = reports.find(r => r.reportItems.find(w => w.reportWidgetID === payload));
  if (report) {
    yield put(selectReport(report.reportID));
  }
}

const getLoadReportDataAction = (reportDataId, startDate, endDate, locationId, reportWidgetID) => {
  const nextDay = moment().add(1, 'days').set('hours', 5).set('minutes', 0)
    .startOf('minutes')
    .unix();

  const isFuture = startDate >= nextDay;

  const payload = {
    reportDataId,
    startDate: isFuture
      ? moment.unix(startDate).subtract(1, 'year').valueOf()
      : startDate * 1000, // times thousand to get from seconds to milliseconds
    endDate: isFuture
      ? moment.unix(endDate).subtract(1, 'year').valueOf()
      : endDate * 1000,
    widgetId: reportWidgetID,
    options: {
      locationId,
    },
  };

  return loadReportDataAction(payload);
};

const filterComparisonDateRanges = (comparisonDateRanges, locations, reportID) => comparisonDateRanges
  .filter((cdr) => {
    if (cdr.option === comparisonOptionNames.none) {
      return false;
    }

    if (cdr.locationId === -1) {
      return true;
    }

    const location = locations.find(l => l.id === cdr.locationId);
    return location ? location.reportAssignments.find(ra => ra.reportId === reportID) : false;
  });

export function* loadAllReportData() {
  const selectedReport = yield select(state => state.reports.selectedReport);
  const selectedLocation = yield select(state => state.reports.selectedLocation);
  if (!selectedReport || !selectedLocation) {
    return;
  }

  const { reportItems, reportID } = selectedReport;
  const { id: locationID } = selectedLocation;
  const { startDate, endDate } = yield select(state => state.filter.dateRange);
  const comparisonDateRanges = yield select(state => state.filter.comparisonDateRanges);
  const comparisonOptions = yield select(state => state.filter.comparisonOptions);
  const locations = yield select(state => state.reports.locations);
  const activeComparisonDateRanges = filterComparisonDateRanges(comparisonDateRanges, locations, reportID);

  for (let i = 0; i < reportItems.length; i++) {
    const { reportWidgetID } = reportItems[i];

    yield put(getLoadReportDataAction(0, startDate, endDate, locationID, reportWidgetID));

    for (let j = 0; j < activeComparisonDateRanges.length; j++) {
      const cdr = activeComparisonDateRanges[j];
      const comparisonDateRange = cdr.option === comparisonOptionNames.custom
        ? cdr : comparisonOptions.find(x => x.name === cdr.option);

      yield put(getLoadReportDataAction(
        cdr.comparisonId,
        comparisonDateRange.startDate,
        comparisonDateRange.endDate,
        cdr.locationId === -1 ? locationID : cdr.locationId,
        reportWidgetID,
      ));
    }
  }
}

export function* putLoadAllReportDataAction() {
  yield put(loadAllReportDataAction());
}

export function* loadReportDataForComparisonId({ payload }) {
  if (payload.option === comparisonOptionNames.none) {
    return;
  }

  const selectedLocation = yield select(state => state.reports.selectedLocation);
  const selectedReport = yield select(state => state.reports.selectedReport);
  const locations = yield select(state => state.reports.locations);


  const location = payload.locationId === -1 ? selectedLocation : locations.find(l => l.id === payload.locationId);
  const locationHasReport = location
    ? location.reportAssignments.map(ra => ra.reportId).includes(selectedReport.reportID)
    : false;

  if (!selectedReport || !locationHasReport) {
    return;
  }
  const { reportItems } = selectedReport;
  const comparisonDateRanges = yield select(state => state.filter.comparisonDateRanges);
  const cdr = comparisonDateRanges.find(x => x.comparisonId === payload.comparisonId);

  for (let i = 0; i < reportItems.length; i++) {
    const { reportWidgetID } = reportItems[i];

    yield put(getLoadReportDataAction(
      cdr.comparisonId, cdr.startDate, cdr.endDate, location.id, reportWidgetID,
    ));
  }
}

export function* selectReportBasedOnLocation() {
  const selectedLocation = yield select(state => state.reports.selectedLocation);
  const selectedReport = yield select(state => state.reports.selectedReport);

  if (selectedReport && selectedLocation) {
    const { reportID } = selectedReport;
    const locationHasReport = selectedLocation.reportAssignments.map(ra => ra.reportId).includes(reportID);
    if (locationHasReport) {
      yield put(selectReport(reportID));
    } else {
      const [firstReport] = selectedLocation.reportAssignments.map(ra => ra.reportId);
      yield put(selectReport(firstReport));
    }
  }
}
